/**
 * DimensionScores Component - Scores Card by Dimension
 * @module features/results/components/DimensionScores
 *
 * Displays a card with scores organized by dimension.
 * Uses ScoreIndicator internally to show visual score dots.
 */

import { cn } from '@/lib/utils';
import { ScoreIndicator } from './ScoreIndicator';

export interface DimensionScore {
  /** Name of the dimension */
  name: string;
  /** Score value from 0 to 100 */
  score: number;
}

export interface DimensionScoresProps {
  /** Overall score (0-100) */
  overall?: number;
  /** Array of dimension scores */
  dimensions: DimensionScore[];
}

/**
 * DimensionScores displays a card with scores by dimension
 *
 * @example
 * ```tsx
 * <DimensionScores
 *   overall={72}
 *   dimensions={[
 *     { name: 'Produto', score: 85 },
 *     { name: 'Tech', score: 60 },
 *   ]}
 * />
 * ```
 */
export function DimensionScores({ overall, dimensions }: DimensionScoresProps) {
  return (
    <div
      data-testid="dimension-scores-card"
      className={cn('rounded-lg border bg-card', 'p-4 md:p-6', 'shadow-sm')}
    >
      {/* Overall Score (optional) */}
      {overall !== undefined && (
        <div className="mb-4 pb-4 border-b border-border">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-muted-foreground">Pontuacao Geral</span>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold">{overall}</span>
              <ScoreIndicator score={overall} size="md" />
            </div>
          </div>
        </div>
      )}

      {/* Dimension List */}
      <ul data-testid="dimension-list" className="flex flex-col gap-3">
        {dimensions.map((dimension) => (
          <li key={dimension.name} className="flex items-center justify-between py-1">
            <span className="text-sm text-foreground">{dimension.name}</span>
            <ScoreIndicator score={dimension.score} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export default DimensionScores;
